import TeamMember from './TeamMember';
import TeamInvite from './TeamInvite';
import {TeamMemberRole} from './TeamMember';
import {sortBy} from '../util/array';
import {mediaTypeLabels} from 'bigdatr-theme';
import {SupportedCountry} from '../util/SupportedCountry';

/** AU advertising compliance codes as returned on team `Compliance` product (wire shape). */
export const TEAM_AUSTRALIA_COMPLIANCE_CODE_KEYS = [
    'AANA_CODE_OF_ETHICS',
    'AANA_FOOD_AND_BEVERAGES_ADVERTISING_CODE',
    'AANA_CHILDRENS_ADVERTISING_CODE',
    'AANA_ENVIRONMENTAL_CLAIMS_CODE',
    'AANA_WAGERING_ADVERTISING_CODE',
    'FCAI_CODE'
] as const;

export type TeamAustraliaComplianceCodeKey = (typeof TEAM_AUSTRALIA_COMPLIANCE_CODE_KEYS)[number];

export type TeamAustraliaComplianceWire = Partial<
    Record<TeamAustraliaComplianceCodeKey, string[] | null>
>;

export type ProductType =
    | 'TeamAdmin'
    | 'MediaValue'
    | 'Termatico'
    | 'AdvertisingCreative'
    | 'DataEntry'
    | 'Compliance';

export type Product = {
    product: ProductType;
    industryIds?: string[] | null;
    industries?: string[] | null;
    mediaTypeIds?: string[] | null;
    mediaTypes?: string[] | null;
    status: TeamProductStatus;
    metadata?: {trialEnd?: string} | null;
    country: SupportedCountry;
    /** Set when `product === 'Compliance'` (AU). Mirrors team GraphQL `compliance`. */
    compliance?: {AU?: TeamAustraliaComplianceWire | null} | null;
};

export enum TeamProductStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    TRIALING = 'TRIALING'
}

const {INACTIVE, TRIALING} = TeamProductStatus;

export type TeamInput = {
    id: string;
    name: string;
    status?: string | null;
    members?: Array<TeamMember | null> | null;
    invites?: Array<TeamInvite | null> | null;
    products?: Array<Product | null> | null;
    trialExpired?: boolean | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    hasTrialTeamName?: boolean | null;
    downloadCount?: number | null;
    requestedNzTrial?: boolean | null;
};

export type MediaTypeAccessOption = {
    type?: string | null;
    id?: string | null;
    hasAccess?: boolean | null;
};

export default class Team {
    id: string;
    name: string;
    status: string | null;
    members: Array<TeamMember>;
    invites: Array<TeamInvite>;
    products: Array<Product>;
    productMap: {
        [C in SupportedCountry]?: {
            [P in ProductType]?: Product;
        };
    };
    trialExpired: boolean | null;
    createdAt: string | null;
    updatedAt: string | null;
    hasTrialTeamName: boolean | null;
    downloadCount: number | null;
    requestedNzTrial: boolean | null;

    constructor(input: TeamInput) {
        this.id = input.id;
        this.name = input.name;
        this.status = input.status ?? null;
        this.trialExpired = input.trialExpired ?? null;
        this.createdAt = input.createdAt ?? null;
        this.updatedAt = input.updatedAt ?? null;
        this.hasTrialTeamName = input.hasTrialTeamName ?? null;
        this.downloadCount = input.downloadCount ?? null;
        this.requestedNzTrial = input.requestedNzTrial ?? null;
        this.members = (input.members || []).map((m: any) => new TeamMember(m));
        this.invites = (input.invites || []).map((m: any) => new TeamInvite(m));
        this.products = input.products?.filter((product): product is Product => !!product) || [];
        this.productMap = this.products.reduce((map, product) => {
            map[product.country] ||= {};
            // need to do assign products to a country like this because otherwise typescript cries
            const countryMap = map[product.country];
            if (countryMap) countryMap[product.product] = product;
            return map;
        }, {} as typeof this.productMap);
    }

    /**
     * Helper to check whether you can edit (change role, remove) a member of a team
     *
     * This is useful when complying with the rule of always having one owner on a team
     */
    canEditMember(member: TeamMember) {
        return this.canEditMemberRole(member, 'OWNER') || this.canEditMemberRole(member, 'MEMBER');
    }

    canEditMemberRole(member: TeamMember, role: TeamMemberRole) {
        if (this.id !== member.teamId) return false;

        switch (role) {
            case 'OWNER': {
                const hasMultipleOwners =
                    this.members.filter((member) => member.role === 'OWNER').length > 1;
                return member.role === 'OWNER' && hasMultipleOwners;
            }
            case 'MEMBER':
                return member.role === 'MEMBER';
            default:
                return false;
        }
    }

    //
    // Products

    get australianProducts() {
        return this.productMap.Australia ?? {};
    }

    get newZealandProducts() {
        return this.productMap['New Zealand'] ?? {};
    }

    get hasNoPermissions() {
        return this.products.length === 0 || this.products.every((p) => p.status === INACTIVE);
    }

    get hasMoreThanOneCountry() {
        return (this.hasAusCreative || this.hasAusMediaValue) && this.hasNzCreative;
    }

    get isAdminOnlyTeam() {
        return (
            (this.hasTeamAdmin || this.hasDataEntry || this.hasTermatico) &&
            !this.hasAusMediaValue &&
            !this.hasNzCreative &&
            !this.hasAusCreative
        );
    }

    get hasTeamAdmin() {
        return Boolean(this.productMap.Australia?.TeamAdmin);
    }

    get hasTermatico() {
        return Boolean(this.productMap.Australia?.Termatico);
    }

    get hasAusCreative() {
        const product = this.australianProducts.AdvertisingCreative;
        if (!product || product.status === INACTIVE) return false;
        if (product.status === TRIALING) return (product.industryIds || []).length > 0;
        return true;
    }

    get hasNzCreative() {
        const product = this.newZealandProducts.AdvertisingCreative;
        if (!product || product.status === INACTIVE) return false;
        return true;
    }

    get hasAusMediaValue() {
        const product = this.australianProducts.MediaValue;
        if (!product || product.status === INACTIVE) return false;
        if (product.status === TRIALING) return (product.industryIds || []).length > 0;
        return true;
    }

    get hasNzMediaValue() {
        const product = this.newZealandProducts.MediaValue;
        if (!product || product.status === INACTIVE) return false;
        if (product.status === TRIALING) return (product.industryIds || []).length > 0;
        return true;
    }

    get hasDataEntry() {
        return Boolean(this.productMap.Australia?.DataEntry);
    }

    get countriesWithAccess() {
        const activeOrTrialingCustomerProducts = this.products.filter(
            (p) =>
                ['MediaValue', 'AdvertisingCreative'].includes(p.product) &&
                p.status !== TeamProductStatus.INACTIVE
        );
        return [...new Set(activeOrTrialingCustomerProducts.map((p) => p.country))];
    }

    get trialEndDate() {
        const longestTrialEndDate = sortBy(
            this.products,
            (pp) => {
                if (pp?.metadata?.trialEnd) return new Date(pp.metadata.trialEnd).getTime();
                return '';
            },
            true
        )[0];

        return longestTrialEndDate?.metadata?.trialEnd;
    }

    get isTrialNotExpired() {
        return this.trialEndDate
            ? new Date().getTime() < new Date(this.trialEndDate).getTime()
            : false;
    }

    get mediaValueIndustryNames() {
        return this.productMap.Australia?.MediaValue?.industries ?? [];
    }

    get isAnyCustomerProductOnTrial() {
        const customerProducts = this.products.filter(
            (p) => p.product === 'AdvertisingCreative' || p.product === 'MediaValue'
        );
        return customerProducts.some((p) => {
            return p.status === TRIALING;
        });
    }

    advertisingCreativeIndustryNames(country: SupportedCountry) {
        return this.productMap[country]?.AdvertisingCreative?.industries ?? [];
    }

    mediaValueIsAllowedIndustry(industryId: string) {
        if (!this.productMap.Australia?.MediaValue) return false;
        const industries = this.productMap.Australia?.MediaValue?.industryIds || [];
        return industries.length === 0 || industries.includes(industryId) || industryId === 'All';
    }

    getMediaTypeAccess({
        option,
        productKey,
        country
    }: {
        option: MediaTypeAccessOption;
        productKey: Extract<ProductType, 'AdvertisingCreative' | 'MediaValue'>;
        country: SupportedCountry;
    }): boolean {
        let isMediaTypeLocked = option.hasAccess === false;

        if (option.type === 'mediaType') {
            // Media type gating is product-specific; determine if this option should be locked.
            const mediaTypeIds = this.productMap[country]?.[productKey]?.mediaTypeIds ?? [];

            // Only gate when the option has an id.
            // If gating isn't configured, this should not be treated as locked.
            if (!option.id) {
                return false;
            }

            isMediaTypeLocked = !mediaTypeIds.includes(option.id);
        }

        return isMediaTypeLocked;
    }

    isMediaTypeAllowed({
        mediaTypeId,
        productKey,
        country
    }: {
        mediaTypeId: string;
        productKey: Extract<ProductType, 'AdvertisingCreative' | 'MediaValue'>;
        country: SupportedCountry;
    }): boolean {
        const mediaTypeIdsFromPermissions =
            this.productMap[country]?.[productKey]?.mediaTypeIds ?? [];
        return mediaTypeIdsFromPermissions.includes(mediaTypeId);
    }

    getMediaTypeNamesAndSynonyms(input: {
        product: 'AdvertisingCreative' | 'MediaValue';
        country: SupportedCountry;
    }): string[] {
        const mediaTypesFromProduct = this.productMap[input.country]?.[input.product]?.mediaTypes;
        if (!mediaTypesFromProduct) return [];

        const mediaTypeNamesAndSynonymsFromProduct = new Set<string>();

        mediaTypesFromProduct.forEach((canonicalName) => {
            mediaTypeNamesAndSynonymsFromProduct.add(canonicalName);
            const synonym = mediaTypeLabels[canonicalName];
            if (synonym) mediaTypeNamesAndSynonymsFromProduct.add(synonym);
        });

        return Array.from(mediaTypeNamesAndSynonymsFromProduct);
    }
}
